import React, { useCallback, useMemo, useState } from "react";
import { ActionIcon, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconTrash } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { getStainDeletionEligibility } from "../../services/stainDeletionEligibility.service";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useDeleteStain } from "../../hooks/useStainRequest";
import { DeleteStainModal } from "./DeleteStainModal";

/** The subset of a slide this button needs, from any of the slide payloads. */
export interface DeletableStainSlide {
  id: string;
  number?: string | null;
  status?: string | null;
  isCompleted?: boolean | null;
  stainingStartedAt?: string | null;
  hl7MessageId?: string | null;
  panelOrderComponentId?: string | null;
  stain?: { code?: string | null } | null;
}

/**
 * Histology surfaces (cutting, staining, case detail). On the pathologist-review
 * surface the caller passes the narrower pair instead — see StainsCenter.
 */
export const HISTOLOGY_STAIN_DELETE_PERMISSIONS = ["stains.delete"];

/**
 * Removing a stain during pathologist review is restricted to administrators and
 * medical directors, mirroring `pathologist_review.specimen_delete` (PR #356).
 */
export const PATHOLOGIST_REVIEW_STAIN_DELETE_PERMISSIONS = [
  "pathologist_review.write",
  "pathologist_review.stain_delete",
];

/** Mirrors the backend guard, which refuses once a case is signed or billed. */
export const DEFAULT_DISABLED_REASON =
  "This case is signed off or locked for billing. Stains can only be changed through the correction/amendment workflow.";

interface Props {
  slide: DeletableStainSlide;
  caseId: string;
  caseNumber?: string;
  /** Human-readable position, e.g. "A-1-2". Falls back to the slide number. */
  slideLabel?: string;
  disabled?: boolean;
  allowPostSignOffEvaluation?: boolean;
  evaluateEligibility?: boolean;
  /** Shown on hover when disabled, so the greyed-out icon explains itself. */
  disabledReason?: string;
  /** ALL must be held. Defaults to the histology-side permission. */
  requiredPermissions?: string[];
}

/**
 * Self-contained stain delete action. Owns its own permission gate, confirm
 * modal and mutation so every screen that shows a slide can drop it in without
 * repeating the wiring.
 */
export const DeleteStainButton: React.FC<Props> = ({
  slide,
  caseId,
  caseNumber,
  slideLabel,
  disabled,
  allowPostSignOffEvaluation = false,
  evaluateEligibility = false,
  disabledReason,
  requiredPermissions = HISTOLOGY_STAIN_DELETE_PERMISSIONS,
}) => {
  const canDeleteStain = useHasPermission(...requiredPermissions);
  const canDeleteAfterSignOff = useHasPermission("stains.delete_post_signoff");
  const loadDeletionEligibility = useCallback(
    () => getStainDeletionEligibility(slide.id),
    [slide.id]
  );
  const eligibility = useQuery({
    queryKey: ["stain-deletion-eligibility", caseId, slide.id],
    queryFn: loadDeletionEligibility,
    enabled: (canDeleteStain || canDeleteAfterSignOff) && evaluateEligibility,
    staleTime: 0,
    retry: false,
  });
  const [opened, { open, close }] = useDisclosure(false);
  const [reason, setReason] = useState("");
  const deleteStain = useDeleteStain();
  const isDisabled = useMemo(
    () =>
      (evaluateEligibility &&
        (eligibility.isError ||
          eligibility.isFetching ||
          !eligibility.data?.eligible)) ||
      (!!disabled &&
        !(
          allowPostSignOffEvaluation &&
          canDeleteAfterSignOff &&
          eligibility.data?.eligible
        )),
    [
      disabled,
      allowPostSignOffEvaluation,
      canDeleteAfterSignOff,
      evaluateEligibility,
      eligibility.data,
      eligibility.isFetching,
      eligibility.isError,
    ]
  );

  // stainingStartedAt and hl7MessageId are both stamped the moment an order goes
  // out, so they fire earlier than the status transition.
  const stainingStarted = useMemo(
    () =>
      slide.status === "staining_in_progress" ||
      slide.status === "staining_completed" ||
      !!slide.stainingStartedAt ||
      !!slide.hl7MessageId ||
      !!slide.isCompleted,
    [
      slide.status,
      slide.stainingStartedAt,
      slide.hl7MessageId,
      slide.isCompleted,
    ]
  );

  const stainCode = useMemo(
    () => slide.stain?.code ?? "No stain",
    [slide.stain]
  );
  const resolvedLabel = useMemo(
    () => slideLabel ?? slide.number ?? "",
    [slideLabel, slide.number]
  );

  const handleOpen = useCallback(() => {
    setReason("");
    open();
  }, [open]);

  const handleClose = useCallback(() => {
    if (deleteStain.isPending) return;
    close();
  }, [close, deleteStain.isPending]);

  const handleConfirm = useCallback(async () => {
    try {
      await deleteStain.mutateAsync({ slideId: slide.id, caseId, reason });
      close();
      notifications.show({
        title: "Stain deleted",
        message: `${stainCode} slide removed from the case.`,
        color: "green",
        autoClose: 4000,
      });
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      const message =
        status === 409
          ? // The backend guard text explains exactly which lock applies.
            (error as Error).message
          : status === 403
          ? "You do not have permission to delete stains."
          : "Couldn't delete this stain. Try again, or contact support if it keeps failing.";
      notifications.show({
        title: "Delete failed",
        message,
        color: "red",
        autoClose: 6000,
      });
    }
  }, [deleteStain, slide.id, caseId, reason, close, stainCode]);

  if (!canDeleteStain && !canDeleteAfterSignOff) return null;

  const trigger = (
    <ActionIcon
      variant="subtle"
      color="red"
      size="sm"
      onClick={handleOpen}
      disabled={isDisabled}
      loading={deleteStain.isPending}
      aria-label={`Delete ${stainCode} stain`}
    >
      <IconTrash size={14} />
    </ActionIcon>
  );

  return (
    <>
      {isDisabled ? (
        // A disabled ActionIcon swallows pointer events, so the tooltip never
        // fires unless the wrapper carries them. Same pattern as PrintStickerButton.
        <Tooltip
          label={
            eligibility.data?.reason ??
            disabledReason ??
            DEFAULT_DISABLED_REASON
          }
          withArrow
          multiline
          maw={260}
        >
          <div style={{ display: "inline-block", cursor: "not-allowed" }}>
            {trigger}
          </div>
        </Tooltip>
      ) : (
        <Tooltip label="Delete stain" withArrow>
          {trigger}
        </Tooltip>
      )}

      <DeleteStainModal
        opened={opened}
        stainCode={stainCode}
        slideLabel={resolvedLabel}
        caseNumber={caseNumber}
        stainingStarted={stainingStarted}
        reason={reason}
        isDeleting={deleteStain.isPending}
        onReasonChange={setReason}
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
    </>
  );
};
